import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule, formatDate } from "@angular/common";
import {
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  FormGroup,
} from '@angular/forms';
import {MatCardModule} from '@angular/material/card';
import {MatButtonModule} from '@angular/material/button';
import {MatIconModule} from '@angular/material/icon';
import {MatInputModule} from '@angular/material/input';
import {MatSelectModule} from '@angular/material/select';
import {MatDialogModule, MatDialog} from '@angular/material/dialog';
import {MatTooltipModule} from '@angular/material/tooltip';
import { Subject, pipe} from 'rxjs';
import { distinctUntilChanged, map, takeUntil } from 'rxjs/operators';
import { Agreement } from '@models/agreement.model';
import { Store } from "@ngrx/store";
import { AgGridAngular } from "ag-grid-angular";
import type { ColDef} from "ag-grid-community";

import { selectAgreementForm,
    selectAgreementEmail,
    selectAgreements,
    selectLoadingAgreements,
    selectDocuSignLoading,
    selectUploadedDocuments,
    selectLoadingDocuments,
    selectIsVoidingAgreement,
  } from '@store/selectors/agreement/agreement.selectors';
import  { standardTheme } from '@styles/ag-grid-theme';
import { AgreementActions } from '@store/actions/agreement/agreement.actions';
import { ConnectFormDirective } from '@directives/connect-form/connect-form.directive';
import { IconRendererComponent } from '@components/grid-components/icon-renderer/icon-renderer.component';
import { agGridIconParams } from '@models/ag-grid-icon-params.model';
import { AgreementActionRendererComponent } from '@components/grid-components/agreement-action-renderer/agreement-action-renderer.component';
import { DocusignActions } from '@store/actions/docusign/docusign.actions';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DocumentUploadModalComponent } from '@components/modals/document-upload-modal/document-upload-modal.component';
import { ConfirmationModalComponent } from '@components/confirmation-modal/confirmation-modal.component';
import { EmailInputDialogComponent } from '@components/modals/email-input-dialog/email-input-dialog.component';
import { selectSendingEnvelope } from '@store/selectors/docusign/docusign.selectors';

@Component({
  selector: 'app-agreement-table',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatInputModule,
    MatSelectModule,
    MatCardModule,
    CommonModule,
    MatButtonModule,
    MatIconModule,
    AgGridAngular,
    ConnectFormDirective,
    MatProgressSpinnerModule,
    MatDialogModule,
    MatTooltipModule,
],
  templateUrl: './agreement-table.component.html',
  styleUrl: './agreement-table.component.css'
})
export class AgreementTableComponent implements OnInit, OnDestroy {

  private readonly store = inject(Store);
  private readonly dialog = inject(MatDialog);
  private ngUnsubscribe = new Subject<void>();
  private readonly standardFormPipe = pipe(
        distinctUntilChanged<any>(),
        takeUntil(this.ngUnsubscribe)
  );
  standardTheme = standardTheme;

  agreementEmail$ = this.store.select(selectAgreementEmail);
  colDefs = this.getColDefs();
  data$ = this.store.select(selectAgreements);
  tableLoading$ = this.store.select(selectLoadingAgreements);
  form$ = this.store.select(selectAgreementForm);
  docuSignLoadingAction$ = this.store.select(selectDocuSignLoading);
  uploadedDocuments$ = this.store.select(selectUploadedDocuments);
  documentsLoading$ = this.store.select(selectLoadingDocuments);
  creatingDocument$ = this.store.select(selectSendingEnvelope);
  isVoidingAgreement$ = this.store.select(selectIsVoidingAgreement);
  documentColDefs = this.getDocumentColDefs();

  private agreements: Agreement[] = [];

  mustVoidFirst$ = this.data$.pipe(
    map(agreements => agreements?.some(a =>
      a.signedStatus === 'sent' || a.signedStatus === 'delivered') ?? false)
  );

  hasExistingAgreement$ = this.data$.pipe(
    map(agreements => agreements?.some(a =>
      a.signedStatus === 'completed' || a.signedStatus === 'voided' ||
      a.signedStatus === 'declined' || a.signedStatus === 'timedOut' ||
      a.signedStatus === 'authenticationFailed') ?? false)
  );

  agreementForm: FormGroup = new FormGroup({
      year: new FormControl(0),
  });

  defaultColDef: ColDef = {
    flex: 1,
    filter: false,
    editable: false,
  };

  ngOnInit() {
    this.agreementForm.controls['year'].valueChanges.pipe(
      this.standardFormPipe
    ).subscribe((value: number) => {
      this.store.dispatch(AgreementActions.searchYearChanged({ year: value }));
    });
    this.data$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(agreements => {
      this.agreements = agreements ?? [];
    });
    this.store.dispatch(AgreementActions.refreshButtonPressed());
    this.loadDocuments();
  }

  private isTerminalStatus(status: string): boolean {
    return status === 'completed' || status === 'voided' || status === 'declined' ||
           status === 'timedOut' || status === 'authenticationFailed';
  }

  private openReplaceConfirmation(): Promise<boolean> {
    return new Promise(resolve => {
      const dialogRef = this.dialog.open(ConfirmationModalComponent, {
        data: {
          title: 'Replace Existing Agreement',
          content: 'Creating a new agreement will permanently delete the existing agreement. This action cannot be undone. Are you sure you want to continue?',
          confirmText: 'Create New Agreement',
          cancelText: 'Cancel',
        },
      });
      dialogRef.afterClosed().subscribe(confirmed => resolve(!!confirmed));
    });
  }

  refreshPage() {
    this.store.dispatch(AgreementActions.refreshButtonPressed());
  }

  loadDocuments() {
    this.store.dispatch(AgreementActions.loadDocuments());
  }

  openUploadModal() {
    const dialogRef = this.dialog.open(DocumentUploadModalComponent, {
      maxWidth: '90vw',
      maxHeight: '90vh',
      panelClass: 'upload-modal-panel'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.loadDocuments();
      }
    });
  }

  getYears() {
    let currentYear = new Date().getFullYear();
    let startAt = 2025;
    let size = currentYear - startAt + 1;

    return [...Array(size).keys()].map(i => i + startAt).reverse();
  }

  openAgreementFunction(envelopeId: any) {
    this.store.dispatch(AgreementActions.openInDocusignPressed({ envelopeId }));
  }

  downloadOrgAgreement(envelopeId: string) {
    this.store.dispatch(AgreementActions.downloadOrgAgreement({ envelopeId }));
  }

  voidOrgAgreement(envelopeId: string) {
    const dialogRef = this.dialog.open(ConfirmationModalComponent, {
      data: {
        title: 'Void Agreement',
        content: 'Are you sure you want to void this agreement? This action cannot be undone.',
        confirmText: 'Void Agreement',
        cancelText: 'Cancel',
      },
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.store.dispatch(AgreementActions.voidOrgAgreement({ envelopeId }));
      }
    });
  }

  async sendViaEmail() {
    const hasExisting = this.agreements.some(a => this.isTerminalStatus(a.signedStatus));
    if (hasExisting) {
      const confirmed = await this.openReplaceConfirmation();
      if (!confirmed) return;
    }

    const emailDialogRef = this.dialog.open(EmailInputDialogComponent, {
      width: '400px',
      data: {
        title: 'Send Agreement via Email',
        message: 'Enter the name and email address of the person who should sign the agreement:',
      },
    });

    emailDialogRef.afterClosed().subscribe((result: { email: string; name: string } | null) => {
      if (result?.email) {
        this.store.dispatch(AgreementActions.sendOrgAgreementWithEmail({ email: result.email, name: result.name }));
      }
    });
  }

  downloadDocument(documentId: number) {
    this.store.dispatch(AgreementActions.downloadDocument({ documentId }));
  }

  getStatusText(status: string): string {
    switch (status) {
      case 'completed': return 'Signed';
      case 'sent':
      case 'delivered': return 'Awaiting Signature';
      case 'voided': return 'Voided';
      case 'declined': return 'Declined';
      case 'timedOut': return 'Expired';
      case 'authenticationFailed': return 'Auth Failed';
      default: return status;
    }
  }

  getStatusColor(status: string): string {
    switch (status) {
      case 'completed': return 'green';
      case 'sent':
      case 'delivered': return 'orange';
      case 'voided':
      case 'declined':
      case 'timedOut':
      case 'authenticationFailed': return 'red';
      default: return 'inherit';
    }
  }

  getColDefs() {
    return [
      {
        field: 'name',
        editable: false,
        headerName: 'Name',
        suppressNavigable: true,
        sortable: false,
        flex: 2,
      },
      {
        field: 'signedStatus',
        headerName: 'Status',
        editable: false,
        valueFormatter: (p: any) => this.getStatusText(p.value),
        cellStyle: (p: any) => ({ color: this.getStatusColor(p.value) }),
        sortable: false,
      },
      {
        headerName: 'Signed By / Assigned To',
        editable: false,
        sortable: false,
        valueGetter: (p: any) => {
          const status = p.data?.signedStatus;
          if (status === 'sent' || status === 'delivered') {
            const name = p.data?.assignedTo || '';
            const email = p.data?.assignedToEmail ? ` (${p.data.assignedToEmail})` : '';
            return (name + email) || '-';
          }
          return p.data?.signedBy || '-';
        },
      },
      {
        field: 'signedOn',
        headerName: 'Signed On',
        editable: false,
        sortable: false,
        tooltipValueGetter: (p: any) => p.value ? formatDate(p.value, 'short', 'en-us') : '',
        valueFormatter: (p: any) =>
          p.value ? formatDate(p.value, 'short', 'en-us') : '',
      },
      {
        headerName: 'Action',
        editable: false,
        sortable: false,
        flex: 2,
        cellStyle: { display: 'flex', alignItems: 'center' },
        cellRenderer: AgreementActionRendererComponent,
        cellRendererParams: (p: any) => ({
          envelopeId: p.data?.envelopeId,
          signedStatus: p.data?.signedStatus,
          isVoiding: false,
          hasDownloadableDocument: p.data?.hasDownloadableDocument,
          onSign: this.openAgreementFunction.bind(this),
          onVoid: this.voidOrgAgreement.bind(this),
          onDownload: this.downloadOrgAgreement.bind(this),
        }),
      },
    ];
  }

  getDocumentColDefs() {
    return [
      {
        field: "fileName",
        headerName: "Document Name",
        editable: false,
        flex: 2,
        sortable: true
      },
      {
        field: "uploadedBy",
        headerName: "Uploaded By",
        editable: false,
        flex: 1.5,
        sortable: true
      },
      {
        field: "uploadedAt",
        headerName: "Upload Date",
        editable: false,
        flex: 1.5,
        sortable: true,
        valueFormatter: (p: any) =>
          p.value ? formatDate(p.value, 'short', 'en-us') : '',
      },
      {
        field: "documentId",
        headerName: "Actions",
        editable: false,
        flex: 1,
        cellRenderer: IconRendererComponent,
        cellRendererParams: (params: any) => {
          let icon: agGridIconParams = {
            icon: "download",
            onClick: () => this.downloadDocument(params.data.documentId)
          }
          return icon;
        },
        sortable: false
      }
    ]
  }

  async createEnvelope() {
    const hasExisting = this.agreements.some(a => this.isTerminalStatus(a.signedStatus));
    if (hasExisting) {
      const confirmed = await this.openReplaceConfirmation();
      if (!confirmed) return;
    }
    this.store.dispatch(DocusignActions.sendDocusignEnvelopeButtonPressed());
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
